import React, { createContext, useContext, useState, useEffect } from 'react';
import { cartService } from './cartService';

const CartContext = createContext();

const getBgForType = (type) => {
  switch (type?.toLowerCase()) {
    case 'starter':
      return 'linear-gradient(135deg, #A8E6CF 0%, #3D8B7A 80%)';
    case 'bronze':
      return 'linear-gradient(135deg, #D69A6C 0%, #A9673A 80%)';
    case 'silver':
      return 'linear-gradient(135deg, #D8DEDF 0%, #8F9A9C 80%)';
    case 'gold':
      return 'linear-gradient(135deg, #F0D18B 0%, #B9852E 80%)';
    case 'platinum':
      return 'linear-gradient(135deg, #E5E9F0 0%, #A3BE8C 80%)';
    default:
      return 'linear-gradient(135deg, #D69A6C 0%, #A9673A 80%)';
  }
};

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('isoret_cart');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => ({
            id: item.couponId,
            name: `${item.couponType} Digital Card`,
            price: item.couponPrice,
            tier: item.couponType,
            quantity: item.quantity,
            bg: getBgForType(item.couponType)
          }));
        }
      } catch (e) {
        console.error('Error parsing cart from localStorage:', e);
      }
    }
    return [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [apiCartCount, setApiCartCount] = useState(0);
  const [token, setToken] = useState(() => localStorage.getItem('token') || sessionStorage.getItem('token'));

  // Sync token state with storage during render to detect login/logout
  const currentToken = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (currentToken !== token) {
    setToken(currentToken);
  }

  useEffect(() => {
    if (!token) {
      const localFormat = cartItems.map((item) => ({
        couponId: item.id,
        couponType: item.tier,
        couponPrice: item.price,
        quantity: item.quantity
      }));
      localStorage.setItem('isoret_cart', JSON.stringify(localFormat));
    }
  }, [cartItems, token]);

  useEffect(() => {
    if (token) {
      const saved = localStorage.getItem('isoret_cart');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const payload = parsed.map((item) => ({
              couponId: item.couponId,
              quantity: item.quantity,
            }));
            cartService.syncCart(payload)
              .then((res) => {
                if (res && res.success) {
                  localStorage.removeItem('isoret_cart');
                  // Fetch updated count
                  cartService.getCartItemCount()
                    .then((countRes) => {
                      if (countRes && countRes.success) {
                        setApiCartCount(countRes.data);
                      }
                    })
                    .catch((err) => console.error('Error fetching count after sync:', err));
                  // Fetch updated cart items
                  cartService.getCart()
                    .then((cartRes) => {
                      if (cartRes && cartRes.success && Array.isArray(cartRes.data)) {
                        const mappedItems = cartRes.data.map((item) => ({
                          id: item.couponId,
                          name: `${item.couponType} Digital Card`,
                          price: item.couponPrice,
                          tier: item.couponType,
                          quantity: item.quantity,
                          bg: getBgForType(item.couponType),
                        }));
                        setCartItems(mappedItems);
                      }
                    })
                    .catch((err) => console.error('Error fetching cart after sync:', err));
                }
              })
              .catch((err) => console.error('Error syncing cart:', err));
          } else {
            localStorage.removeItem('isoret_cart');
          }
        } catch (e) {
          console.error('Error parsing cart from localStorage during sync:', e);
          localStorage.removeItem('isoret_cart');
        }
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      cartService.getCartItemCount()
        .then((res) => {
          if (res && res.success) {
            setApiCartCount(res.data);
          }
        })
        .catch((err) => console.error('Error fetching cart item count:', err));
    }
  }, [token, cartItems]);

  useEffect(() => {
    if (token) {
      cartService.getCart()
        .then((res) => {
          if (res && res.success && Array.isArray(res.data)) {
            const mappedItems = res.data.map((item) => ({
              id: item.couponId,
              name: `${item.couponType} Digital Card`,
              price: item.couponPrice,
              tier: item.couponType,
              quantity: item.quantity,
              bg: getBgForType(item.couponType)
            }));
            setCartItems(mappedItems);
          }
        })
        .catch((err) => console.error('Error fetching cart:', err));
    }
  }, [token, isCartOpen]);

  const addToCart = async (item) => {
    const existing = cartItems.find((i) => i.id === item.id);
    if (existing && existing.quantity >= 3) {
      return;
    }
    if (token) {
      try {
        const res = await cartService.addToCart(item.id);
        if (res && res.success) {
          setCartItems((prevItems) => {
            const existing = prevItems.find((i) => i.id === item.id);
            const cappedQty = Math.min(res.data.quantity, 3);
            if (existing) {
              return prevItems.map((i) =>
                i.id === item.id ? { ...i, quantity: cappedQty } : i
              );
            }
            return [...prevItems, { ...item, quantity: cappedQty, bg: item.bg || getBgForType(item.tier) }];
          });
          // Update count
          const countRes = await cartService.getCartItemCount();
          if (countRes && countRes.success) {
            setApiCartCount(countRes.data);
          }
        }
      } catch (err) {
        console.error('Error adding to cart:', err);
      }
    } else {
      setCartItems((prevItems) => {
        const existing = prevItems.find((i) => i.id === item.id);
        if (existing) {
          if (existing.quantity >= 3) return prevItems;
          return prevItems.map((i) =>
            i.id === item.id ? { ...i, quantity: Math.min(i.quantity + 1, 3) } : i
          );
        }
        return [...prevItems, { ...item, quantity: 1 }];
      });
    }
  };

  const removeFromCart = async (itemId) => {
    if (token) {
      try {
        const res = await cartService.removeItem(itemId);
        if (res && res.success) {
          setCartItems((prevItems) => prevItems.filter((i) => i.id !== itemId));
          // Update count
          const countRes = await cartService.getCartItemCount();
          if (countRes && countRes.success) {
            setApiCartCount(countRes.data);
          }
        }
      } catch (err) {
        console.error('Error removing item from cart:', err);
      }
    } else {
      setCartItems((prevItems) => prevItems.filter((i) => i.id !== itemId));
    }
  };

  const updateQuantity = async (itemId, newQty) => {
    if (newQty > 3) return;
    if (token) {
      const item = cartItems.find((i) => i.id === itemId);
      if (!item) return;

      const isIncrease = newQty > item.quantity;
      try {
        if (isIncrease) {
          if (item.quantity >= 3) return;
          const res = await cartService.increaseQty(itemId);
          if (res && res.success) {
            const cappedQty = Math.min(res.data.quantity, 3);
            setCartItems((prevItems) =>
              prevItems.map((i) => (i.id === itemId ? { ...i, quantity: cappedQty } : i))
            );
          }
        } else {
          const res = await cartService.decreaseQty(itemId);
          if (res && res.success) {
            if (res.data && res.data.quantity > 0) {
              setCartItems((prevItems) =>
                prevItems.map((i) => (i.id === itemId ? { ...i, quantity: res.data.quantity } : i))
              );
            } else {
              setCartItems((prevItems) => prevItems.filter((i) => i.id !== itemId));
            }
          }
        }
        // Update count
        const countRes = await cartService.getCartItemCount();
        if (countRes && countRes.success) {
          setApiCartCount(countRes.data);
        }
      } catch (err) {
        console.error('Error updating quantity:', err);
      }
    } else {
      if (newQty <= 0) {
        removeFromCart(itemId);
        return;
      }
      setCartItems((prevItems) =>
        prevItems.map((i) => (i.id === itemId ? { ...i, quantity: Math.min(newQty, 3) } : i))
      );
    }
  };

  const clearCart = async () => {
    if (token) {
      try {
        const res = await cartService.clearCart();
        if (res && res.success) {
          setCartItems([]);
          setApiCartCount(0);
        }
      } catch (err) {
        console.error('Error clearing cart:', err);
      }
    } else {
      setCartItems([]);
    }
  };

  const localCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const cartCount = token ? apiCartCount : localCount;
  const cartTotal = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        isCartOpen,
        setIsCartOpen,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

